'use memo';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { TextVariant } from '@/components/Typography/Text.variant';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { genNameInitials } from '@/utils/name';
import useGroupJoin from '../../hooks/useGroupJoin';
import useGroupLeave from '../../hooks/useGroupLeave';
import type { AppUser, AppUserGroupRef } from '../../types';
type GroupProvisioningDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  softwareId: string;
  appIntegrationId: string;
  appUser: AppUser;
  groups: AppUserGroupRef[];
  mode: 'join' | 'leave';
};
export function GroupProvisioningDialog({
  open,
  onOpenChange,
  softwareId,
  appIntegrationId,
  appUser,
  groups,
  mode
}: GroupProvisioningDialogProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.profile.app.users'
  });
  const {
    t: tDomain
  } = useTranslation(defaultNS, {
    keyPrefix: 'domain.appUser'
  });
  const tc = useCommonTranslation();
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(() => new Set(groups.map(g => g.id)));

  // ダイアログが開かれたタイミング（false → true の遷移）で必ず初期状態に戻す。
  // Radix Dialog の onOpenChange は親が制御的に open=true に変えたときには
  // 発火しないため、open の遷移を useEffect で検知してリセットする。
  // ref で前回の open を保持することで、開いている最中に親側が refetch して
  // groups が入れ替わった場合にはユーザーの選択を上書きしない。
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedGroupIds(new Set(groups.map(g => g.id)));
    }
    prevOpenRef.current = open;
  }, [open, groups]);
  const handleToggle = useCallback((groupId: string, checked: boolean) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(groupId);
      } else {
        next.delete(groupId);
      }
      return next;
    });
  }, []);
  const groupJoin = useGroupJoin({
    softwareId,
    appUserId: appUser.id,
    appIntegrationId
  });
  const groupLeave = useGroupLeave({
    softwareId,
    appUserId: appUser.id,
    appIntegrationId
  });
  const mutation = mode === 'join' ? groupJoin : groupLeave;
  const isPending = mutation.isPending;
  const handleSubmit = useCallback(async () => {
    const ids = Array.from(selectedGroupIds);
    try {
      const result = await mutation.mutateAsync(ids);
      const failedGroups = result.results.filter(r => !r.success);
      const allFailed = result.results.length > 0 && failedGroups.length === result.results.length;
      if (allFailed) {
        const failedNames = failedGroups.map(r => r.groupName).join(', ');
        toast({
          title: t('dialog.groupProvisioningAllFailed', {
            groups: failedNames
          })
        });
        return;
      }
      if (failedGroups.length > 0) {
        const failedNames = failedGroups.map(r => r.groupName).join(', ');
        toast({
          title: t('dialog.groupProvisioningPartialError', {
            groups: failedNames
          })
        });
      } else {
        toast({
          title: t('dialog.groupProvisioningSuccess')
        });
      }
      onOpenChange(false);
    } catch {
      // onError in the mutation hook handles toast
    }
  }, [selectedGroupIds, mutation, onOpenChange, t]);
  const titleKey = mode === 'join' ? 'dialog.groupJoin.title' : 'dialog.groupLeave.title';
  const descriptionKey = mode === 'join' ? 'dialog.groupJoin.description' : 'dialog.groupLeave.description';
  const hasSelection = selectedGroupIds.size > 0;
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
        </DialogHeader>

        <VStack className="gap-4">
          <VStack className="gap-2">
            <Text variant={TextVariant.labelSmallReg} className="text-neutral-muted-on-fill">
              {tDomain('appUser')}
            </Text>
            <HStack className="gap-3 items-center">
              <Avatar className="h-10 w-10 bg-neutral-muted-fill">
                <AvatarImage className="object-contain" src={appUser.avatarUrl} />
                <AvatarFallback>
                  {genNameInitials(appUser.name || '')}
                </AvatarFallback>
              </Avatar>
              <VStack className="min-w-0 flex-1 gap-1">
                <Text variant={TextVariant.labelReg} className="line-clamp-1">
                  {appUser.name}
                </Text>
                <Text variant={TextVariant.labelSmallReg} className="line-clamp-1 text-neutral-muted-on-fill">
                  {appUser.email}
                </Text>
              </VStack>
            </HStack>
          </VStack>

          <VStack className="gap-2">
            <Text variant={TextVariant.labelSmallReg} className="text-neutral-muted-on-fill">
              {t(descriptionKey)}
            </Text>
            {groups.map(group => <Checkbox key={group.id} checked={selectedGroupIds.has(group.id)} onCheckedChange={checked => handleToggle(group.id, checked)} label={group.name} disabled={isPending} />)}
          </VStack>
        </VStack>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {tc('action.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!hasSelection || isPending}>
            {tc('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}